import {Injectable, Injector} from '@angular/core';
import {ApolloLink, FetchResult, Observable} from '@apollo/client/core';
import {onError} from '@apollo/client/link/error';
import {Store} from '@ngrx/store';
import {refreshToken as refreshAction} from '@shared/store/auth/auth.actions';
import {AuthService} from '@app/public/auth/service/auth.service';
import {HttpErrorResponse} from '@angular/common/http';
import {environment} from '../../../environments/environment';
import {TokenStorageService} from './token-storage.service';

@Injectable({
    providedIn: 'root',
})
export class ErrorLinkService {
    private authService: AuthService | undefined; // Lazy-loaded AuthService

    constructor(private injector: Injector, private store: Store, private tokenStorage: TokenStorageService) { }

    createErrorLink(): ApolloLink {
        return onError(({ graphQLErrors, operation, forward, response, networkError }) => {
          if (!environment.production) {
            console.error("Error occurred", graphQLErrors, response, networkError)
          }
          let errors = null
          if (graphQLErrors) {
            for (const err of graphQLErrors) {
              switch (err.message) {
                case 'input: no user specified':
                  return this.handleTokenExpiry(operation, forward);
              }
            }
            errors = graphQLErrors
          }
          if (networkError && networkError instanceof HttpErrorResponse && networkError.message.includes("422")) {
            errors = networkError.error.errors;
          }
          if (networkError && networkError instanceof HttpErrorResponse && networkError.status == 401) {
            // todo: Logout User
          }

          // Only transform errors that need validation processing
          const needsValidationProcessing = errors?.some((error: any) =>
            error.extensions?.code === 'BAD_USER_INPUT' ||
            error.extensions?.validation ||
            error.extensions?.exception?.validation
          );

          if (!needsValidationProcessing) {
            // Return original error if it's not a validation error
            return forward(operation);
          }

          // Process errors to human-friendly format
          let errorsProcessed = {}
          if (errors) {
            errors.forEach((error) => {
              let {message, path} = error
              let lastPath = errorsProcessed
              for (let i = 1; i < path.length; i++) {
                if (i == 0 && (path[i] == 'variable' || path[i] == operation.operationName) ) continue

                if (!lastPath[path[i]]) {
                  lastPath[path[i]] = {}
                  if (i == (path.length - 1)) {
                    lastPath[path[i]] = []
                  }
                  lastPath = lastPath[path[i]]
                } else {
                  lastPath = lastPath[path[i]]
                }

                if (i == (path.length - 1) && lastPath instanceof Array) {
                  let m = message
                  if (m == "cannot be null") {
                    m = "This field is required"
                  }
                  lastPath.push(m)
                }
              }
            })
          }

          if (Object.keys(errorsProcessed).length > 0) {
            return new Observable<FetchResult>((observer) => {
              observer.next({
                errors: [{
                  message: "VALIDATION_ERROR",
                  extensions: {
                    errors: errorsProcessed
                  }
                }],
                data: null
              });
              observer.complete();
            });
          }
          return forward(operation);
        });
    }

    handleTokenExpiry(operation, forward) {
        const refreshToken = this.tokenStorage.getCurrentRefreshToken() || '';

        if (!this.authService) {
            this.authService = this.injector.get(AuthService); // Lazy load AuthService to avoid circular dependencies
        }

        // Return a new Observable that will complete when the refresh operation completes
        return new Observable<FetchResult>((observer) => {
            this.authService!
                .refreshToken({ refreshToken: refreshToken })
                .subscribe({
                    next: (response) => {
                        const newToken = response.data.refreshToken;
                        if (newToken) {
                            this.store.dispatch(refreshAction({ token: newToken }));
                        }
                        operation.setContext({ // attaching the new headers
                            headers: {
                                ...operation.getContext()['headers'],
                                authorization: `Bearer ${newToken}`,
                            },
                        });

                        forward(operation).subscribe({
                            next: (result) => observer.next(result),
                            error: (error) => {
                                this.authService.logout()
                                observer.error(error)
                            },
                            complete: () => observer.complete(),
                        });
                    },
                    error: (refreshError) => {
                        observer.error(refreshError);
                        this.authService.logout();
                    },
                });
        });
    }
}
